import React, { useMemo } from "react";
import {
  Button,
  Collapse,
  ConfigProvider,
  Empty,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { buildFreightDocumentsByStoppingPoint } from "../../../util/freight/freight-document-utils";
import theme from "../../../theme";

const { Text } = Typography;

const antdModalTheme = {
  token: {
    colorPrimary: theme.colors.primary,
    colorText: theme.colors.textPrimary,
    colorTextSecondary: theme.colors.textSecondary,
    colorBorder: theme.colors.primaryBorder,
    borderRadius: theme.radius.small,
    fontFamily: theme.typography.fontFamily,
  },
};

function resolveFileName(document) {
  const fromUrl = String(document?.url || "")
    .split("?")[0]
    .split("/")
    .pop();

  if (fromUrl) return decodeURIComponent(fromUrl);
  return `${document?.label || "documento"}-${document?.name || "arquivo"}`;
}

function openDocument(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function downloadDocument(document) {
  if (!document?.url) return;

  const link = window.document.createElement("a");
  link.href = document.url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = resolveFileName(document);
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
}

function FreightDocumentsModal({
  open,
  onClose,
  loading = false,
  freightData,
  freightLabel,
}) {
  const sections = useMemo(
    () => buildFreightDocumentsByStoppingPoint(freightData || {}),
    [freightData],
  );

  const defaultActiveKeys = useMemo(
    () => sections.map((section) => section.id),
    [sections],
  );

  const modalTitle = freightLabel
    ? `Documentos da fatura — ${freightLabel}`
    : "Documentos da fatura";

  return (
    <ConfigProvider theme={antdModalTheme}>
      <Modal
        open={open}
        onCancel={onClose}
        title={modalTitle}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Fechar
          </Button>,
        ]}
        width={760}
        destroyOnClose
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
            paddingTop: 8,
          },
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <Spin size="large" tip="Carregando documentos..." />
          </div>
        ) : sections.length === 0 ? (
          <Empty description="Nenhum ponto de parada encontrado para este frete." />
        ) : (
          <Collapse
            bordered={false}
            defaultActiveKey={defaultActiveKeys}
            items={sections.map((section) => ({
              key: section.id,
              label: (
                <Space size={8} wrap>
                  <Text strong>{section.title}</Text>
                  <Tag color={section.documents.length > 0 ? "blue" : "default"}>
                    {section.documents.length}{" "}
                    {section.documents.length === 1 ? "arquivo" : "arquivos"}
                  </Tag>
                </Space>
              ),
              children:
                section.documents.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Não há arquivos cadastrados neste ponto de parada."
                  />
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={section.documents}
                    renderItem={(document) => (
                      <List.Item
                        actions={[
                          <Tooltip key="view" title="Visualizar documento">
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              disabled={!document.canView}
                              onClick={() => openDocument(document.url)}
                            >
                              Visualizar
                            </Button>
                          </Tooltip>,
                          <Tooltip key="download" title="Baixar documento">
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              disabled={!document.canDownload}
                              onClick={() => downloadDocument(document)}
                            >
                              Baixar
                            </Button>
                          </Tooltip>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <FileTextOutlined
                              style={{ fontSize: 18, color: theme.colors.primary }}
                            />
                          }
                          title={document.label}
                          description={document.name}
                        />
                      </List.Item>
                    )}
                  />
                ),
            }))}
          />
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default FreightDocumentsModal;
